import {
  AlertBanner,
  API_CONFIG,
  Blogs,
  KycStatus,
  Page,
  PromotionBannerContent,
  PromotionModalContent,
  PtFAQ,
  SignupModalContent,
  WhatsNewInfo,
} from '@msrllabs/pt-api';
import { genUuid } from '@msrllabs/pt-api/utils';

import { emptyPage, fetchJson } from './apiUtils';
import { ApiError, unwrapApiError } from './errors';
import { searchDollarTagInstrument } from './marketData';

export const search = (indices: string[] | null, q: string, isSaveTrending: boolean, from = 0, size = 10, signal?: AbortSignal) => {
  if (indices != null && indices.length === 1 && indices[0] === 'instrument') {
    return searchDollarTagInstrument(q, from, size, signal);
  }

  let url: string;
  if (indices == null) {
    url = `/aqt/api/v1/search?`;
  } else {
    url = `/aqt/api/v1/search?indices=${encodeURIComponent(indices.join(','))}&`;
  }
  return fetchJson<{
    totalHits: number;
    hits: { score: number; index: string; id: string; sourceAsMap: any }[];
  }>(`${API_CONFIG.PT_WEB_ENDPOINT}${url}q=${encodeURIComponent(q)}&from=${from}&size=${size}&isSaveTrending=${isSaveTrending}`, {
    signal,
  });
};

export const getAlertBanners = async (
  _accountType: string,
  _kycStatus: KycStatus,
  _page = 0,
  _size = 10
): Promise<Page<AlertBanner>> => {
  return emptyPage();
};

export const getBlogs = async (_pageNumber = 1, _pageSize: number = 10, _isDesc = true): Promise<Blogs> => {
  return emptyPage();
};

export const getPromotionalModalContents = async (
  _isWeb: boolean = true,
  _isDesc: boolean = true,
  _page: number = 0,
  _size: number = 1
): Promise<Page<PromotionModalContent>> => {
  return emptyPage();
};

export const getPromotionBannerContents = async (): Promise<Page<PromotionBannerContent>> => {
  return emptyPage();
};

export const getServerTime = () => {
  return fetchJson<number>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/api/v1/now`);
};

export const getSignUpModalContents = async (): Promise<Page<SignupModalContent>> => {
  return emptyPage();
};

export const getTrendingSearches = async () => {
  return fetchJson<string[]>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/api/v1/search/trending`);
};

export const getWhatsNewInfo = async (_pageNumber: number, _pageSize = 10, _isDesc = true): Promise<WhatsNewInfo> => {
  return emptyPage();
};

export const getFAQContents = async (): Promise<PtFAQ> => {
  // FAQ content is bundled as a static file (mirrors the iOS app's bundled q_and_a_v2.json),
  // so it doesn't depend on the marketing API being reachable / CORS-enabled for the web origin.
  const response = await fetch('/labels/faq.json');
  const content = await response.json();
  return { content, totalElements: 0, totalPages: 1 };
};

export const sendFeedback = async (email: string, content: string, type: string, imageIds: string[]) => {
  return fetchJson<{ responseMessage: string }>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/api/v1/createFeedback`, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ content: content, type: type, email: email, imageIds: imageIds }),
  });
};

export const uploadFeedbackImage = async (image: File, filename: string) => {
  let formData = new FormData();
  formData.append('content', image);
  formData.append('filename', filename);
  return fetchJson<{
    contentType: string;
    filename: string;
    height: number;
    id: string;
    length: number;
    uploadDate: Date;
    width: number;
  }>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/api/v1/uploadFeedbackImage`, {
    method: 'POST',
    headers: { Authorization: await API_CONFIG.accessToken },
    body: formData,
  });
};

/** Carries which of the two calls failed, so the form can say which one. */
export class SupportRequestError extends Error {
  constructor(
    readonly stage: 'upload' | 'send',
    readonly apiError: ApiError
  ) {
    super(`Support request failed while ${stage === 'upload' ? 'uploading screenshots' : 'sending the message'}`);
  }
}

/**
 * Screenshots first, then the request itself — the server takes image ids, not the files.
 * Nothing is created until `sendFeedback` resolves, so a retry after a failure sends exactly one request.
 */
export const submitSupportRequest = async (email: string, content: string, type: string, files: File[]) => {
  let stage: 'upload' | 'send' = 'upload';
  try {
    const images = await Promise.all(files.map(file => uploadFeedbackImage(file, genUuid())));
    stage = 'send';
    return await sendFeedback(
      email,
      content,
      type,
      images.map(image => image.id)
    );
  } catch (e) {
    throw new SupportRequestError(stage, unwrapApiError(e));
  }
};
